import React from "react";
import { GoChevronLeft } from "react-icons/go";
import { useFormik, FormikHelpers } from "formik";
import get from "lodash/get";

//components
import Button from "@/shared/Button";
import Select from "@/shared/Select";
import Input from "@/shared/Input";
import notification from "@/components/notifications";

//helpers
import campaigns from "@/api/query/campaigns";
import purposes from "@/api/query/purposes";
import { validationSchemaCreateCampaign } from "@/helpers/schemas/validationSchemaCreateCampaign";

// interfaces
import { ICampaign } from "@/types/api/Campaign";

// assets
import styles from "@/assets/styles/containers/shortlist-form.module.scss";


interface CreateNewCampaignFormProps {
  toggleForm: () => void;
  space: { id: number }; // Replace with the actual type of space
  onSuccess: (data: ICampaign) => Promise<void>;
}

interface IFormValues {
  name: string;
  purpose: { label: string; value: number };
}

const CreateNewCampaignForm = ({ toggleForm, space, onSuccess }: CreateNewCampaignFormProps) => {

  const { mutateAsync: quickCreate } = campaigns.useQuickCreate();
  const { mutateAsync: postCampaignItem } = campaigns.usePostCampaignItem();
  const { data: purposesOptions = [] } = purposes.usePurposesList();

  const options = purposesOptions.map(purpose => ({ label: purpose.name, value: purpose.id }));

  async function onSubmit(data: IFormValues, formMethods: FormikHelpers<IFormValues>) {
    try {
      const payload = { ...data, purpose: Number(data.purpose.value) };
      const res = await quickCreate(payload);
      await postCampaignItem({ id: res.data.id, data: { space: space.id } });
      await onSuccess(res.data);
      toggleForm();
    }
    catch (error) {
      const errorMessage = get(error, "response.data.name");

      if (errorMessage) {
        formMethods.setFieldError("name", errorMessage);

        return;
      }

      notification({
        type: "error",
        placement: "bottomLeft",
        duration: 5,
        message: "Oops",
        description: "Something went wrong, please try again.",
      });
    }
  }

  const { values, handleSubmit, setFieldValue, errors, isSubmitting } = useFormik<IFormValues>(
    {
      initialValues: {
        name: "",
        purpose: options[0]
      },
      validationSchema: validationSchemaCreateCampaign,
      validateOnBlur: false,
      validateOnChange: true,
      validateOnMount: false,
      enableReinitialize: true,
      onSubmit: onSubmit
    });

  return (
    <form onSubmit={handleSubmit}>
      <div
        className={styles["locl-shortlist-form_go-back"]}
        onClick={toggleForm}
        data-testid="locl-shortlist-form-go-back"
      >
        <GoChevronLeft/>
        Create new campaign
      </div>
      <div className={styles["locl-shortlist-form__create-campaign"]} data-testid="locl-shortlist-form-create-campaign">
        <p className={styles["locl-shortlist-form__create-campaign-title"]}>Campaign Name</p>
        <Input
          size="large"
          placeholder="Create campaign name"
          value={values.name}
          error={errors.name}
          onChange={e => setFieldValue("name", e.target.value)}
          data-testid="locl-shortlist-form-create-input"
        />
        <p className={styles["locl-shortlist-form__create-campaign-title_grey"]}>What</p>
        <Select
          fullWidth={true}
          options={options}
          value={values.purpose}
          onChange={(e) => {
            setFieldValue("purpose", e);
          }}
          className="locl-shortlist-form__create-campaign__select"
          aria-label="locl-shortlist-form-create-select"
        />
      </div>
      <div className={styles["locl-shortlist-form__create-campaign_controls"]}>
        <Button
          onClick={toggleForm}
          type="secondary"
          data-testid="locl-shortlist-form-create-button-cancel"
        >
          Cancel
        </Button>
        <Button
          htmlType="submit"
          loading={isSubmitting}
          data-testid="locl-shortlist-form-create-button-save"
        >
          Save and continue
        </Button>
      </div>
    </form>
  );
};

export default CreateNewCampaignForm;
